// EloTrendChart — tiny SVG sparkline for a silo's ELO over the last 30
// days. Lightweight (no chart lib), warm aesthetic, calm. Shows summary
// W/L/D counts beneath the line. Used on contender profile cards inside
// the Find Opponents / Battle Hall views.

import { useEffect, useState } from "react";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PADDING = { top: 6, right: 4, bottom: 6, left: 4 };
const DEFAULT_W = 200;
const DEFAULT_H = 56;

export default function EloTrendChart({ siloId, days = 30, width = DEFAULT_W, height = DEFAULT_H, showSummary = true, testid }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!siloId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/battles/silos/${siloId}/elo-history`, { params: { days } });
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [siloId, days]);

  if (err) return null;
  if (!data) return <div className="text-[10px] tpl-muted">Loading…</div>;

  const pts = data.points || [];
  const summary = data.summary || { wins: 0, losses: 0, draws: 0, total: 0 };

  if (pts.length < 2) {
    return (
      <div data-testid={testid} className="space-y-1">
        <div className="flex items-center gap-1.5 text-[10px] tpl-muted">
          <Minus size={10} />
          <span>{data.current_elo ? `ELO ${Math.round(data.current_elo)}` : "Not battled yet"}</span>
        </div>
        {showSummary && summary.total > 0 && (
          <div className="text-[10px] tpl-muted">
            {summary.wins}W · {summary.losses}L · {summary.draws}D in {days}d
          </div>
        )}
      </div>
    );
  }

  const elos = pts.map((p) => p.elo);
  const minE = Math.min(...elos);
  const maxE = Math.max(...elos);
  const range = Math.max(2, maxE - minE);
  const innerW = width - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;
  const xStep = pts.length > 1 ? innerW / (pts.length - 1) : 0;
  const xy = pts.map((p, i) => {
    const x = PADDING.left + i * xStep;
    const y = PADDING.top + innerH - ((p.elo - minE) / range) * innerH;
    return { x, y, elo: p.elo, outcome: p.outcome, delta: p.delta };
  });
  const path = xy.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const first = elos[0];
  const last = elos[elos.length - 1];
  const trend = last - first;
  const TrendIcon = trend > 5 ? TrendUp : trend < -5 ? TrendDown : Minus;
  const trendColor = trend > 5 ? "#4A5D4E" : trend < -5 ? "#C16E5A" : "#6B6A68";

  return (
    <div data-testid={testid} className="space-y-1">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block" role="img" aria-label={`ELO trend over ${days} days`}>
        {/* Background gridline at midpoint */}
        <line x1={PADDING.left} x2={width - PADDING.right} y1={PADDING.top + innerH / 2} y2={PADDING.top + innerH / 2} stroke="#E8E5DF" strokeDasharray="2,3" />
        {/* Trend line */}
        <path d={path} fill="none" stroke={trendColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Points (outcome colored) */}
        {xy.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2.2}
            fill={p.outcome === "win" ? "#4A5D4E" : p.outcome === "loss" ? "#C16E5A" : "#A89360"}
          >
            <title>{`${p.outcome.toUpperCase()} · ELO ${Math.round(p.elo)} (${p.delta >= 0 ? "+" : ""}${Math.round(p.delta)})`}</title>
          </circle>
        ))}
      </svg>
      {showSummary && (
        <div className="flex items-center justify-between text-[10px] tpl-muted tabular-nums">
          <span className="flex items-center gap-1" style={{ color: trendColor }}>
            <TrendIcon size={11} weight="bold" />
            {Math.round(last)} <span className="opacity-60">({trend >= 0 ? "+" : ""}{Math.round(trend)})</span>
          </span>
          <span>{summary.wins}W · {summary.losses}L · {summary.draws}D</span>
        </div>
      )}
    </div>
  );
}
